import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AccountProvider, useAccount, useAuth, useMeQuery, useMyAccountsQuery, type Area } from '@/app/session';
import { AppShell } from '@/components/layout/AppShell';
import { EmptyState, ErrorState, PageLoader, ButtonLink } from '@/components/ui';
import { configured } from '@/lib/env';
import { safeNext } from '@/pages/auth/safeNext';

const Login = lazy(() => import('@/pages/auth/Login'));
const Signup = lazy(() => import('@/pages/auth/Signup'));
const Onboarding = lazy(() => import('@/pages/onboarding/Onboarding'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const ListingsPage = lazy(() => import('@/pages/listings/ListingsPage'));
const ListingEditor = lazy(() => import('@/pages/listings/ListingEditor'));
const ImportPage = lazy(() => import('@/pages/listings/ImportPage'));
const FeedsPage = lazy(() => import('@/pages/listings/FeedsPage'));
const LeadsPage = lazy(() => import('@/pages/leads/LeadsPage'));
const CampaignsPage = lazy(() => import('@/pages/campaigns/CampaignsPage'));
const CampaignWizard = lazy(() => import('@/pages/campaigns/CampaignWizard'));
const CampaignDetail = lazy(() => import('@/pages/campaigns/CampaignDetail'));
const BillingPage = lazy(() => import('@/pages/billing/BillingPage'));
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage'));
const StorefrontPage = lazy(() => import('@/pages/storefront/StorefrontPage'));
const TeamPage = lazy(() => import('@/pages/team/TeamPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminOverview = lazy(() => import('@/pages/admin/AdminOverview'));
const AdminPricing = lazy(() => import('@/pages/admin/AdminPricing'));
const AdminCredits = lazy(() => import('@/pages/admin/AdminCredits'));
const AdminAccounts = lazy(() => import('@/pages/admin/AdminAccounts'));
const AdminReviews = lazy(() => import('@/pages/admin/AdminReviews'));
const AdminAudit = lazy(() => import('@/pages/admin/AdminAudit'));
const NotFound = lazy(() => import('@/pages/NotFound'));

function RequireAuth({ children }: { children: ReactNode }) {
  const { session, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <PageLoader />;
  if (!session) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

function PublicOnly({ children }: { children: ReactNode }) {
  const { session, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <PageLoader />;
  if (session) return <Navigate to={safeNext(new URLSearchParams(loc.search).get('next'))} replace />;
  return <>{children}</>;
}

/** Signed in AND has at least one marketplace account → the app shell. */
function AccountGate() {
  const me = useMeQuery();
  const accounts = useMyAccountsQuery();
  if (me.isPending || accounts.isPending) return <PageLoader />;
  if (me.isError) return <ErrorState error={me.error} onRetry={() => void me.refetch()} />;
  if (accounts.isError) return <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} />;
  if (!accounts.data.length) return <Navigate to="/onboarding" replace />;
  return (
    <AccountProvider me={me.data} accounts={accounts.data}>
      <ShellOutlet />
    </AccountProvider>
  );
}

function ShellOutlet() {
  const loc = useLocation();
  const wide = loc.pathname.startsWith('/leads');
  return (
    <AppShell wide={wide}>
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}

function RequireArea({ area, children }: { area: Area; children: ReactNode }) {
  const { can, role } = useAccount();
  if (!can(area)) {
    return (
      <EmptyState icon="lock" title="You don’t have access to this" actions={<ButtonLink to="/">Back to dashboard</ButtonLink>}>
        Your role on this account ({role}) doesn’t include {area}. Ask an owner or admin to change your role.
      </EmptyState>
    );
  }
  return <>{children}</>;
}

function RequireStaff({ children }: { children: ReactNode }) {
  const { isStaff } = useAccount();
  if (!isStaff) {
    return (
      <EmptyState icon="shield" title="LowOps staff only" actions={<ButtonLink to="/">Back to dashboard</ButtonLink>}>
        The admin area is limited to LowOps staff.
      </EmptyState>
    );
  }
  return <>{children}</>;
}

function NotConfigured() {
  return (
    <div style={{ maxWidth: 560, margin: '12vh auto', padding: 24 }}>
      <EmptyState icon="settings" title="Portal not configured">
        Set <code>VITE_SUPABASE_URL</code> and <code>VITE_SUPABASE_ANON_KEY</code> (see <code>.env.example</code>), or run with
        <code> VITE_MOCK=1</code> for the demo backend.
      </EmptyState>
    </div>
  );
}

const area = (a: Area, el: ReactNode) => <RequireArea area={a}>{el}</RequireArea>;

export default function App() {
  if (!configured) return <NotConfigured />;
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
          <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
          <Route element={<RequireAuth><AccountGate /></RequireAuth>}>
            <Route index element={<Dashboard />} />
            <Route path="listings" element={area('listings', <ListingsPage />)} />
            <Route path="listings/new" element={area('listings', <ListingEditor />)} />
            <Route path="listings/import" element={area('listings', <ImportPage />)} />
            <Route path="listings/feeds" element={area('listings', <FeedsPage />)} />
            <Route path="listings/:id" element={area('listings', <ListingEditor />)} />
            <Route path="leads" element={area('leads', <LeadsPage />)} />
            <Route path="leads/:leadId" element={area('leads', <LeadsPage />)} />
            <Route path="campaigns" element={area('campaigns', <CampaignsPage />)} />
            <Route path="campaigns/new" element={area('campaigns', <CampaignWizard />)} />
            <Route path="campaigns/:id" element={area('campaigns', <CampaignDetail />)} />
            <Route path="campaigns/:id/edit" element={area('campaigns', <CampaignWizard />)} />
            <Route path="billing" element={area('billing', <BillingPage />)} />
            <Route path="analytics" element={area('analytics', <AnalyticsPage />)} />
            <Route path="storefront" element={area('storefront', <StorefrontPage />)} />
            <Route path="team" element={area('team', <TeamPage />)} />
            <Route path="settings" element={area('settings', <SettingsPage />)} />
            <Route path="admin" element={<RequireStaff><AdminLayout /></RequireStaff>}>
              <Route index element={<AdminOverview />} />
              <Route path="pricing" element={<AdminPricing />} />
              <Route path="credits" element={<AdminCredits />} />
              <Route path="accounts" element={<AdminAccounts />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="audit" element={<AdminAudit />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
